import HyperDX from "@hyperdx/browser";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import { API_BASE_URL } from "@/api/config";

export type AnalyticsClient = {
  enabled: boolean;
  track: (event: string, properties?: Record<string, unknown>) => void;
  // Note: identify only accepts userId for GDPR compliance - no email/PII in analytics
  identify: (userId: string) => void;
  reset: () => void;
};

const AnalyticsContext = createContext<AnalyticsClient | null>(null);

const noopClient: AnalyticsClient = {
  enabled: false,
  track: () => {
    // noop
  },
  identify: () => {
    // noop
  },
  reset: () => {
    // noop
  },
};

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const client = useMemo<AnalyticsClient>(() => {
    const env = import.meta.env as Record<string, string | undefined>;
    const environment = env.VITE_APP_ENV || env.MODE;
    const apiKey = env.VITE_HYPERDX_API_KEY;

    // Only enable HyperDX in production
    if (environment !== "production" || !apiKey) {
      return noopClient;
    }
    HyperDX.init({
      apiKey,
      service: env.VITE_HYPERDX_SERVICE || "markeroo-web",
      tracePropagationTargets: [/api/i],
      consoleCapture: true,
      advancedNetworkCapture: true,
      url: `${API_BASE_URL}/telemetry`,
    });
    return {
      enabled: true,
      track: (event, properties) => {
        HyperDX.addAction(event, properties || {});
      },
      identify: (userId) => {
        // Only userId is set for GDPR compliance - no email/PII in analytics
        HyperDX.setGlobalAttributes({
          userId,
        });
      },
      reset: () => {
        // HyperDX doesn't have a dedicated reset method
        // Clear global attributes by setting empty values
        HyperDX.setGlobalAttributes({
          userId: "",
        });
      },
    };
  }, []);

  return <AnalyticsContext.Provider value={client}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsClient {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }
  return context;
}
